import React, { useEffect, useContext } from "react";
import { useKeenSlider } from "keen-slider/react";
import "keen-slider/keen-slider.min.css";
import ResizePlugin from "./ResizePlugin";
import MutationPlugin from "./mutationPlugin";
import ThumbnailPlugin from "./thumbnailPlugin";
import AppContext from "../context/appContext";

const thumbnailCSS = `
  .thumbnail .keen-slider__slide {
    cursor: pointer;
    opacity: 0.6;
    transition: opacity 0.2s ease;
    border: 2px solid transparent;
    border-radius: 6px;
  }
  .thumbnail .keen-slider__slide.active {
    opacity: 1;
    border: 2px solid #6565ff;
    border-radius: 6px;
  }
  .thumbnail .keen-slider__slide:hover {
    opacity: 1;
  }
`;

window.addEventListener("error", (e) => {
  if (
    e.message ===
    "ResizeObserver loop completed with undelivered notifications."
  ) {
    e.stopImmediatePropagation();
  }
});

const ModalSlider = ({ images }) => {
  const [sliderRef, instanceRef] = useKeenSlider({ initial: 0 }, [
    ResizePlugin,
  ]);
  const [thumbnailRef] = useKeenSlider(
    {
      initial: 0,
      slides: { origin: "center", perView: 4, spacing: 10 },
    },
    [ThumbnailPlugin(instanceRef), ResizePlugin, MutationPlugin]
  );

  if (!images || images.length === 0) return null;

  return (
    <div className="mb-3">
      <div
        ref={sliderRef}
        className="keen-slider mb-2"
        style={{ borderRadius: "10px", overflow: "hidden" }}
      >
        {images.map((img, idx) => (
          <div className="keen-slider__slide" key={idx}>
            <img
            loading="lazy"
                             src={`https://res.cloudinary.com/l7pafu4a/image/fetch/f_webp/q_60/${img.url}`}

              alt={`slide-${idx}`}
              style={{
                width: "100%",
                maxHeight: "400px",
                objectFit: "cover",
                borderRadius: "10px",
              }}
            />
          </div>
        ))}
      </div>
      {images.length > 1 && (
        <div ref={thumbnailRef} className="keen-slider thumbnail mt-2">
          {images.map((img, idx) => (
            <div
              className="keen-slider__slide"
              key={idx}
              style={{ cursor: "pointer" }}
            >
              <img
                loading="lazy"
                src={`https://res.cloudinary.com/l7pafu4a/image/fetch/f_webp/q_60/${img.url}`}
                alt={`thumb-${idx}`}
                style={{
                  width: "100%",
                  height: "65px",
                  objectFit: "cover",
                  borderRadius: "6px",
                  opacity: 0.6,
                }}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const projectModalId = (_, index) => `portfolioModal-${index}`;

const ProjectModal = ({ project, modalId }) => (
  <div className="modal fade" id={modalId} tabIndex="-1" aria-hidden="true">
    <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
      <div
        className="modal-content"
        style={{
          backgroundColor: "#1e1e1e",
          color: "white",
          border: "none",
          borderRadius: "16px",
        }}
      >
        <div
          className="modal-header"
          style={{ borderBottom: "1px solid #333" }}
        >
          <h5 className="modal-title" style={{ color: "#6565ff" }}>
            {project.title}
          </h5>
          <button
            type="button"
            className="btn-close btn-close-white"
            data-bs-dismiss="modal"
            aria-label="Close"
          />
        </div>
        <div className="modal-body">
          <ModalSlider images={project.images || []} />
          {project.description && (
            <div
              dangerouslySetInnerHTML={{ __html: project.description }}
              style={{ color: "#ccc", lineHeight: "1.8", fontSize: "15px" }}
            />
          )}

          {project.websiteUrl && (
            <div className="text-center mt-4 mb-2">
              <a
                href={project.websiteUrl}
                target="_blank"
                rel="noreferrer"
                style={{
                  display: "inline-block",
                  backgroundColor: "#6565ff",
                  color: "white",
                  padding: "12px 32px",
                  borderRadius: "30px",
                  textDecoration: "none",
                  fontSize: "15px",
                  fontWeight: "600",
                  boxShadow: "0 4px 15px rgba(101,101,255,0.4)",
                  letterSpacing: "0.5px",
                }}
              >
                🌐 Visit Website
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
);

const MainSlider = ({ portfolios }) => {
  const animation = { duration: 50000, easing: (t) => t };
  const [sliderRef] = useKeenSlider(
    {
      loop: true,
      initial: 0,
      slides: {
        origin: "center",
        perView: window.innerWidth < 480 ? 1 : 2,
        spacing: 30,
      },
      created(s) {
        s.moveToIdx(5, true, animation);
      },
      updated(s) {
        s.moveToIdx(s.track.details.abs + 5, true, animation);
      },
      animationEnded(s) {
        s.moveToIdx(s.track.details.abs + 5, true, animation);
      },
    },
    [ResizePlugin, MutationPlugin]
  );

  return (
    <div ref={sliderRef} className="keen-slider">
      {portfolios.map((project, idx) => (
        <div key={idx} className="keen-slider__slide">
          <a href={project.websiteUrl} target="_blank" rel="noreferrer">
            <img
              alt={project.title}
              src={`https://res.cloudinary.com/l7pafu4a/image/fetch/f_webp/q_60/${project.images?.[0]?.url}`}
              className="rounded-3"
              style={{
                width: "100%",
                display: "block",
                cursor: project.websiteUrl ? "pointer" : "default",
              }}
            />
          </a>

          <div style={{ textAlign: "center",}}>
            <button
              className="my-3"
              data-bs-toggle="modal"
              data-bs-target={`#${projectModalId(project, idx)}`}
              style={{
                backgroundColor: "#6565ff",
                color: "white",
                border: "none",
                borderRadius: "20px",
                padding: "6px 18px",
                fontSize: "13px",
                cursor: "pointer",
                boxShadow: "0 2px 8px rgba(0,0,0,0.5)",
              }}
            >
              View Detail
            </button>
          </div>
        </div>
      ))}
    </div>
  );
};

const Projects = () => {
  const { portfolios, fetchPortfolios } = useContext(AppContext);

  useEffect(() => {
    fetchPortfolios();
  }, [fetchPortfolios]);

  return (
    <>
      <style>{thumbnailCSS}</style>
      <div id="portfolio" className="py-5">
        <div className="text-center">
          <h2
            className="pb-3 h5"
            style={{
              fontWeight:"bold",
              textUnderlineOffset: "6px",
              color: "#6565ff",
            }}
          >
            RECENT PROJECTS 
          </h2>
        </div>
        {portfolios.length > 0 ? (
          <MainSlider portfolios={portfolios} />
        ) : (
          <div className="text-center text-secondary py-5">
            <div className="spinner-border text-white" role="status">
              <span className="sr-only">Loading...</span>
            </div>
          </div>
        )}
      </div>

      {portfolios.map((project, index) => (
        <ProjectModal
          key={project._id || project.id || index}
          project={project}
          modalId={projectModalId(project, index)}
        />
      ))}
    </>
  );
};

export default Projects;
